import { CareerProps, LocationProps } from "@/types";
import { firestore } from "../config/firebase.config";
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  getDoc,
  setDoc,
  CollectionReference,
} from "firebase/firestore";
import { fromFirestore, toFirestore, cleanData } from ".";

const careerCollectionName = "/institutes/{0}/faculties/{1}/careers";

class CareerService {
  async getAll(ref: string, active: boolean = true): Promise<CareerProps[]> {
    const collectionRef = collection(firestore, `${ref}/careers`);

    const q = query(collectionRef, where("active", "==", active));
    const querySnapshot = await getDocs(q);
    const careers: CareerProps[] = querySnapshot.docs.map((doc) => {
      return fromFirestore<CareerProps>({
        id: doc.id,
        ...doc.data(),
      } as CareerProps);
    });
    return careers;
  }

  // async getAllByStudyLevel(ref:string, studyLevel:string): Promise <CareerProps[]>{
  //     const collectionRef = collection(firestore,`${ref}/careers`)
  //     const
  // }

  async getById(
    instituteId: string,
    facultyId: string,
    careerId: string
  ): Promise<CareerProps | null> {
    const collectionRef = collection(
      firestore,
      careerCollectionName.replace("{0}", instituteId).replace("{1}", facultyId)
    );
    const docRef = doc(collectionRef, careerId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return fromFirestore<CareerProps>({ ...docSnap.data() } as CareerProps);
    }
    return null;
  }

  async create(
    instituteId: string,
    facultyId: string,
    career: CareerProps,
    careerCode: string
  ): Promise<boolean> {
    try {
      const collectionPath = careerCollectionName
        .replace("{0}", instituteId)
        .replace("{1}", facultyId);
      const documentRef = doc(firestore, collectionPath, careerCode);

      let subscriptionLevel = career.subscriptionLevel;
      if (typeof subscriptionLevel !== "number") {
        try {
          const facultySnap = await getDoc(doc(firestore, `institutes/${instituteId}/faculties`, facultyId));
          if (facultySnap.exists()) {
            subscriptionLevel = facultySnap.data()?.subscriptionLevel ?? 0;
          }
        } catch (e) {
          console.warn("No se pudo obtener el subscriptionLevel de la facultad en create:", e);
        }
      }

      await setDoc(documentRef, {
        ...toFirestore({
          ...career,
          ...(typeof subscriptionLevel === "number" ? { subscriptionLevel } : {}),
        }),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return true;
    } catch (error) {
      console.error("Error creating a new career", error);
      return false;
    }
  }
  async createCareerFromBulk(career: CareerProps, instituteId?: string, facultyId?: string): Promise<boolean> {
    try {
      const instId = instituteId || career.uidInstitution;
      const facId = facultyId || career.uidFaculty;
      const careerCode = career.code;

      if (!instId || !facId || !careerCode) {
        console.error("Faltan parámetros requeridos para crear/actualizar carrera", { instId, facId, careerCode });
        return false;
      }

      const collectionPath = `institutes/${instId}/faculties/${facId}/careers`;
      const documentRef = doc(firestore, collectionPath, careerCode);

      let subscriptionLevel = career.subscriptionLevel;
      if (typeof subscriptionLevel !== "number") {
        try {
          const facultySnap = await getDoc(doc(firestore, `institutes/${instId}/faculties`, facId));
          if (facultySnap.exists()) {
            subscriptionLevel = facultySnap.data()?.subscriptionLevel ?? 0;
          }
        } catch (e) {
          console.warn("No se pudo obtener el subscriptionLevel de la facultad en createCareerFromBulk:", e);
        }
      }

      const careerToSave = {
        ...career,
        id: careerCode,
        uidInstitution: instId,
        uidFaculty: facId,
        ref: `${collectionPath}/${careerCode}`,
        ...(typeof subscriptionLevel === "number" ? { subscriptionLevel } : {}),
      };
      const sanitizedData = cleanData(toFirestore(careerToSave as any));

      const docSnap = await getDoc(documentRef);
      if (docSnap.exists()) {
        await setDoc(
          documentRef,
          {
            ...sanitizedData,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        console.log(`Carrera "${career.name}" con código ${careerCode} actualizada exitosamente en IES ${instId}.`);
      } else {
        await setDoc(documentRef, {
          ...sanitizedData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        console.log(`Carrera "${career.name}" con código ${careerCode} creada exitosamente en IES ${instId}.`);
      }
      return true;
    } catch (error) {
      console.error("Error al crear/actualizar carrera en carga masiva", error);
      return false;
    }
  }

  async updateID(
    instituteId: string,
    facultyId: string,
    careerId: string,
    id: string
  ): Promise<boolean> {
    const collectionRef = collection(
      firestore,
      careerCollectionName.replace("{0}", instituteId).replace("{1}", facultyId)
    );
    const docRef = doc(collectionRef, careerId);

    if (docRef == null) {
      console.error(
        `Error updating: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error(" Doesn't find this career ");
      return false;
    }

    try {
      const updateData = {
        id: id,
      };

      await updateDoc(docRef, updateData);
      console.log(
        `Route: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      return true;
    } catch (error) {
      console.error(
        `Error updating: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error(`updating value: ${id}`);
      console.error(error);
      return false;
    }
  }

  async update(
    instituteId: string,
    facultyId: string,
    careerId: string,
    value: any
  ): Promise<boolean> {
    const collectionRef = collection(
      firestore,
      careerCollectionName.replace("{0}", instituteId).replace("{1}", facultyId)
    );
    const docRef = doc(collectionRef, careerId);

    if (docRef == null) {
      console.error(
        `Error updating: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error(" Doesn't find this career ");
      return false;
    }

    try {
      //convert String to Date
      const datesNames = ["inscriptions", "startYear", "startClass"];
      for (const [name, iValue] of Object.entries(value)) {
        if (datesNames.includes(name)) {
          let newDateValue: Date | null = null;
          if (iValue) {
            try {
              if (iValue instanceof Date) {
                newDateValue = isNaN(iValue.getTime()) ? null : iValue;
              } else if (typeof iValue === "string" && iValue.trim() !== "") {
                const parsed = new Date(iValue);
                newDateValue = isNaN(parsed.getTime()) ? null : parsed;
              }
              console.warn(
                `Start in try parse date ${name} with value ${iValue}`
              );
            } catch (error) {
              console.error(
                `Error in try parse date ${name} with value ${iValue}`
              );
              console.error(error);
              newDateValue = null;
            }
          }
          if (iValue !== undefined) {
            value = {
              ...value,
              [name]: newDateValue,
            };
          }
        }
      }

      //convert values to Number
      const numberNames = ["price", "credits", "duration", "costCredit"];
      for (const [name, iValue] of Object.entries(value)) {
        if (numberNames.includes(name)) {
          let newNumberValue: number | null = null;
          if (iValue !== null && iValue !== undefined && iValue !== "") {
            try {
              if (typeof iValue === "number") {
                newNumberValue = isNaN(iValue) ? null : iValue;
              } else {
                const parsed = Number(iValue);
                newNumberValue = isNaN(parsed) ? null : parsed;
              }
              console.warn(
                `Start in try parse number ${name} with value ${iValue}`
              );
            } catch (error) {
              console.error(
                `Error in try parse number ${name} with value ${iValue}`
              );
              console.error(error);
              newNumberValue = null;
            }
          }
          if (iValue !== undefined) {
            value = {
              ...value,
              [name]: newNumberValue,
            };
          }
        }
      }

      const updateData = cleanData(
        toFirestore({
          ...value,
          updatedAt: new Date(),
        })
      );

      await updateDoc(docRef, updateData);
      console.log(
        `Route: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      return true;
    } catch (error) {
      console.error(
        `Error updating: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error(`updating value: ${value}`);
      console.error(error);
      return false;
    }
  }

  async updateActiveStatus(
    instituteId: string,
    facultyId: string,
    careerId: string,
    value: boolean
  ): Promise<boolean> {
    const collectionRef = collection(
      firestore,
      careerCollectionName.replace("{0}", instituteId).replace("{1}", facultyId)
    );

    const docRef = doc(collectionRef, careerId);
    if (docRef == null) {
      console.error(`Error updating: users/${careerId}`);
      console.error(" Doesn't find this user ");
      return false;
    }
    try {
      await updateDoc(docRef, {
        updatedAt: serverTimestamp(),
        active: value,
      }); // Add a new document with a generated id.
      return true;
    } catch (error) {
      console.error(`Error updating: users/${careerId}`);
      console.error(" Doesn't find this user ");
      return false;
    }
  }

  async updateHeadquarter(
    instituteId: string,
    facultyId: string,
    careerId: string,
    value: LocationProps[]
  ): Promise<boolean> {
    const collectionRef = collection(
      firestore,
      careerCollectionName.replace("{0}", instituteId).replace("{1}", facultyId)
    );
    const docRef = doc(collectionRef, careerId);

    if (docRef == null) {
      console.error(
        `Error updating: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error(" Doesn't find this career ");
      return false;
    }

    try {
      const updateData = {
        headquarter: value,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(docRef, updateData);
      console.log(
        `Route: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      return true;
    } catch (error) {
      console.error(
        `Error updating: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error(`updating value: ${value}`);
      console.error(error);
      return false;
    }
  }

  async updateProfileGraduated(
    instituteId: string,
    facultyId: string,
    careerId: string,
    value: string
  ): Promise<boolean> {
    const collectionRef = collection(
      firestore,
      careerCollectionName.replace("{0}", instituteId).replace("{1}", facultyId)
    );
    const docRef = doc(collectionRef, careerId);

    if (docRef == null) {
      console.error(
        `Error updating: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error(" Doesn't find this career ");
      return false;
    }

    try {
      const updateData = {
        profileGraduated: value,
        updatedAt: serverTimestamp(),
      };
      await updateDoc(docRef, updateData);
      console.log(
        `Route: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      return true;
    } catch (error) {
      console.error(
        `Error updating: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error(`updating value: ${value}`);
      console.error(error);
      return false;
    }
  }

  async updateProgramDescription(
    instituteId: string,
    facultyId: string,
    careerId: string,
    value: string
  ): Promise<boolean> {
    const collectionRef = collection(
      firestore,
      careerCollectionName.replace("{0}", instituteId).replace("{1}", facultyId)
    );
    const docRef = doc(collectionRef, careerId);

    if (docRef == null) {
      console.error(
        `Error updating: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error(" Doesn't find this career ");
      return false;
    }

    try {
      const updateData = {
        description: value,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(docRef, updateData);
      console.log(
        `Route: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      return true;
    } catch (error) {
      console.error(
        `Error updating: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error(`updating value: ${value}`);
      console.error(error);
      return false;
    }
  }

  async delete(
    instituteId: string,
    facultyId: string,
    careerId: string
  ): Promise<boolean> {
    const collectionRef = collection(
      firestore,
      careerCollectionName.replace("{0}", instituteId).replace("{1}", facultyId)
    );
    const docRef = doc(collectionRef, careerId);

    if (docRef.id == null) {
      console.error(
        `Error deleting: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error("Doesn't find Career ");
      return false;
    }

    try {
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error(
        `Error deleting: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error(error);
      return false;
    }
  }

  private async updateMovedCareer(collectionRef: CollectionReference, careerId: string, instituteId: string, facultyId: string): Promise<boolean> {
    const docRef = doc(collectionRef, careerId);

    if (docRef == null) {
      console.error(
        `Error updating: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error(" Doesn't find this career ");
      return false;
    }

    try {
      const updateData = {
        ref: `institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`,
      };

      await updateDoc(docRef, updateData);
      console.log(
        `Route: institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      return true
    } catch (error) {
      console.error(
        `institutes/${instituteId}/faculties/${facultyId}/careers/${careerId}`
      );
      console.error(`updating value: ${careerId}`);
      console.error(error);
      return false;
    }
  }

  async moveCareers(
    instituteId: string,
    fromFacultyId: string,
    toFacultyId: string,
    careers: CareerProps[],
    toFacultyName: string
  ): Promise<boolean> {
    try {
      const collectionPath = careerCollectionName
        .replace("{0}", instituteId)
        .replace("{1}", toFacultyId);
      const movePromises = careers.map(async (career) => {
        const documentRef = doc(firestore, collectionPath, career.code);

        await setDoc(documentRef, {
          ...toFirestore(career),
          status: "Moved",
          prevRef: career.ref,
          facultyName: toFacultyName,
          uidFaculty: toFacultyId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        const collectionRef = collection(
          firestore,
          collectionPath,
        );

        const updateCareer = await this.updateMovedCareer(collectionRef, career.code, instituteId, toFacultyId)

        if (career.id && updateCareer) {
          this.delete(instituteId, fromFacultyId, career.id)
        }
        return { updateCareer };
      });
      const moveResults = await Promise.all(movePromises)
      const allSuccessful = moveResults.every((result) => result.updateCareer);

      return allSuccessful;

    } catch (error) {
      console.error("Error moving a  career", error);
      return false;
    }
  }
}
export default new CareerService();
